import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      // Clear any cached admin status
      setIsAdmin(false);
      fetchDashboardData();
      checkAdminStatus();
    }
  }, [user]);

  const checkAdminStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('User info:', response.data);
      
      // Check if user is admin by querying admin endpoints
      try {
        const adminCheck = await axios.get(`${API}/admin/users`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        // If this succeeds, user is admin
        setIsAdmin(true);
      } catch {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Failed to check admin status:', error);
      setIsAdmin(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const sessionsRes = await axios.get(`${API}/sessions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setRecentSessions(sessionsRes.data.slice(0, 3));
      
      // Set some basic stats
      setStats({
        totalSessions: sessionsRes.data.length,
        completedSessions: 0,
        totalMinutes: 0
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading || loadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mentality-300"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-mentality-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="/mentality-logo.jpg" 
                alt="Mentality Logo" 
                className="w-10 h-10 md:w-12 md:h-12 object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-mentality-300 to-mentality-600 bg-clip-text text-transparent">
                  Mentality
                </h1>
                <p className="text-xs text-gray-500">The College Mentality</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 md:space-x-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="bg-mentality-100 text-mentality-700">Dashboard</Button>
              </Link>
              <Link to="/sessions">
                <Button variant="ghost" size="sm">Sessions</Button>
              </Link>
              <Link to="/mood">
                <Button variant="ghost" size="sm">Mood</Button>
              </Link>
              {isAdmin && (
                <Link to="/admin">
                  <Button variant="ghost" size="sm" className="border border-mentality-300">
                    <span className="hidden md:inline">👑 Admin</span>
                    <span className="md:hidden">👑</span>
                  </Button>
                </Link>
              )}
              <div className="flex items-center space-x-2 md:space-x-3 pl-2 md:pl-4 border-l">
                <Avatar data-testid="user-avatar" className="border-2 border-mentality-200 w-8 h-8 md:w-10 md:h-10">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback className="bg-mentality-100 text-mentality-700">
                    {user.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  data-testid="logout-button"
                  className="border-mentality-200 hover:bg-mentality-50"
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user.name.split(' ')[0]}! 👋
          </h2>
          <p className="text-gray-600">
            Take a moment for yourself today. Your mental wellness matters.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-mentality-300 to-mentality-500 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="pb-3">
              <CardDescription className="text-mentality-50">Total Sessions</CardDescription>
              <CardTitle className="text-4xl font-bold">{stats?.totalSessions || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-mentality-50">Available to explore</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="pb-3">
              <CardDescription className="text-teal-100">Completed</CardDescription>
              <CardTitle className="text-4xl font-bold">{stats?.completed_sessions || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-teal-100">Sessions finished</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="pb-3">
              <CardDescription className="text-cyan-100">Progress</CardDescription>
              <CardTitle className="text-4xl font-bold">{stats?.completion_rate || 0}%</CardTitle>
            </CardHeader>
            <CardContent>
              <Progress value={stats?.completion_rate || 0} className="h-2 bg-cyan-300" />
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-mentality-500 to-mentality-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="pb-3">
              <CardDescription className="text-mentality-100">Mood Entries</CardDescription>
              <CardTitle className="text-4xl font-bold">{stats?.recent_mood_entries || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-mentality-100">Last 7 days</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="border-2 border-mentality-200 shadow-lg hover:shadow-xl transition-all hover:border-mentality-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">🎓</span>
                Start a Session
              </CardTitle>
              <CardDescription>
                Begin your wellness journey with The College Mentality
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/sessions">
                <Button className="w-full bg-gradient-to-r from-mentality-300 to-mentality-500 hover:from-mentality-400 hover:to-mentality-600 text-white" size="lg">
                  Browse All Sessions
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-2 border-teal-200 shadow-lg hover:shadow-xl transition-all hover:border-teal-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">💭</span>
                How are you feeling?
              </CardTitle>
              <CardDescription>
                Track your mood and energy levels
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/mood">
                <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white" size="lg">
                  Log Your Mood
                </Button>
              </Link>
              <p className="text-sm text-gray-500 mt-3 text-center">
                Daily check-ins help track your wellness journey
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Featured Sessions */}
        <Card className="border-2 border-gray-200 shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recommended For You</CardTitle>
                <CardDescription>The College Mentality sessions to boost your wellbeing</CardDescription>
              </div>
              <Link to="/sessions">
                <Button variant="outline" className="border-mentality-300 text-mentality-600 hover:bg-mentality-50">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {recentSessions.map((session) => (
                <Link to={`/sessions/${session.id}`} key={session.id}>
                  <Card className="overflow-hidden hover:shadow-lg transition-all border-2 hover:border-mentality-300 cursor-pointer">
                    <div className="h-48 overflow-hidden">
                      <img
                        src={session.image_url}
                        alt={session.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <Badge className="bg-mentality-100 text-mentality-700 hover:bg-mentality-200">
                          🎓 College Mentality
                        </Badge>
                        <span className="text-sm text-gray-500">{session.duration} min</span>
                      </div>
                      <h3 className="font-semibold text-lg mb-1">{session.title}</h3>
                      <p className="text-sm text-gray-600 line-clamp-2">{session.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DashboardPage;
